import { useState } from "react";
import { useModal } from "../contexts";
import Modal from "./Modal";
import { getData, OAuthError, postData } from "../backend";
import useUser from "../hooks/useUser";
import OAuthModal from "./OAuthModal";
import ErrorModal from "./ErrorModal";
import SmallTextInputField from "./SmallTextInputField";
import SettingsTeamUserRole from "./SettingsTeamUserRole";
import SettingsTeamSeatsModal from "./SettingsTeamSeatsModal";
import { Team, TeamRole } from "../interfaces";
import { XIcon } from "@heroicons/react/outline";
import useSWR from "swr";
import { useTranslation } from "react-i18next";


interface Invite {
  email: string;
  role: TeamRole;

}

function SettingsTeamModalInvite(props: { initialEmail?: string }) {
  const { closeModal, openModal } = useModal();
  const { t } = useTranslation();

  const { user, refreshUser, hasPermission } = useUser();
  const { mutate: revalidateTeam } = useSWR<Team>("/auth/team/", getData);
  const team = user!.activeTeam!;

  const isTeamOwner = user!.id === team.owner;
  const canManageSeats = hasPermission(team, "teams.admin") && isTeamOwner;

  const [invites, setInvites] = useState<Array<Invite>>([{ email: props.initialEmail ?? "", role: "EDITOR" }]);
  const invalidInvites = invites.map((invite, i) => validateEmail(invite.email) ? -1 : i).filter(i => i !== -1);

  const [showInvalidEmails, setShowInvalidEmails] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  function validateEmail(email: string) {
    if (!/[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9.-]{2,}/.test(email))
      return false;

    if (invites.filter(invite => invite.email === email).length > 1)
      return false; // duplicates

    if (team.members.filter(user => user.email === email).length > 0)
      return false; // existing user

    return true;
  }

  function handleUpdateInvite(index: number, updates: Partial<Invite>) {
    const newInvites = [...invites];
    newInvites[index] = { ...newInvites[index], ...updates };

    setInvites(newInvites);
  }

  async function handleInvite() {
    if (invalidInvites.length > 0)
      return setShowInvalidEmails(true);

    setIsInviting(true);

    const newTeamMembers = []

    for (const invite of invites) {
      try {
        const teamUser = await postData("/auth/team/invite/", invite);
        newTeamMembers.push(teamUser);
      } catch (error) {
        setIsInviting(false);

        if (error instanceof OAuthError)
          return openModal(<OAuthModal oauthUrl={error.oauthUrl} />)

        return openModal(<ErrorModal details={error} />)
      }
    }

    await refreshUser();
    await revalidateTeam();

    closeModal();

    // NOTE: Invite no longer created a new user so this modal is useless
    // openModal(<SettingsTeamUserModalAccessBatch />);
  }

  return (
    <Modal
      title={
        <p className="font-gilroy font-semibold text-lg">
          {t("Invite Team Members")} <span className="font-gilroy font-semibold text-gray-600 text-sm">({user?.activeTeam?.members.length}/{user?.activeTeam?.seats})</span>
        </p>
      }
      subtitle={t("Your team workflows and files will be shared with the permissions set by the role selected")}
      secondaryButtonProps={{
        text: t("Cancel"), onClick: () => closeModal()
      }}
      primaryButtonProps={{
        onClick: handleInvite,
        disabled: showInvalidEmails && invalidInvites.length > 0,
        text: <>
          {isInviting ? t("Inviting...") : t("Invite")}
          {invites.length > 1 &&
            <div>({invites.length})</div>
          }
        </>
      }}

    >
      <div className="flex items-start gap-2 mt-4 w-full border-b">
        <div className="font-gilroy font-semibold text-gray-600 text-sm w-8/12">
          {t("Email")}
        </div>
        <div className="font-gilroy font-semibold text-gray-600 text-sm w-3/12">
          {t("Role")}
        </div>
      </div>

      <div className="flex flex-col gap-2 flex-grow py-2 w-full max-h-[400px] overflow-y-scroll">
        {invites.map((invite, i) =>
          <div key={i} className="flex items-center gap-2 w-full">
            <div className="w-8/12">
              <SmallTextInputField value={invite.email} onChange={(email) => handleUpdateInvite(i, { email: email.toLowerCase() })} placeholder={t("Email")} invalid={showInvalidEmails && invalidInvites.includes(i)} autoFocus />
            </div>

            <div className="flex flex-col w-3/12">
              <SettingsTeamUserRole role={invite.role} onSelectRole={(role) => handleUpdateInvite(i, { role })} />
            </div>

            {i > 0 &&
              <XIcon className="w-6 h-6 text-gray-400 hover:text-red cursor-pointer" onClick={() => setInvites(invites.filter((_, j) => j !== i))} />
            }
          </div>
        )}

        <div className="flex items-center gap-4">
          <button className="btn btn-white text-sm py-1 px-2 w-40 flex-shrink-0" onClick={() => setInvites([...invites, { email: "", role: "EDITOR" }])} disabled={(team.members.length + invites.length) >= team.seats}>
            {t("Add another member")}
          </button>

          {(team.members.length + invites.length) >= team.seats &&
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-400">
              {canManageSeats ? (
                <>
                  <span>{t("You have reached your team's seat limit.")}</span>
                  <button
                    type="button"
                    className="text-blue hover:underline"
                    onClick={() => openModal(<SettingsTeamSeatsModal initialSeats={team.seats + 1} />)}
                  >
                    {t("Add seats")}
                  </button>
                </>
              ) : (
                <span>{t("You have reached your team's seat limit. Ask your team owner to add more seats.")}</span>
              )}
            </div>
          }
        </div>

      </div>
    </Modal>
  );
}

export default SettingsTeamModalInvite;
